import { useState, useEffect, useCallback, useMemo } from 'react';
import { useOutletContext } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { ShoppingBag, Plus, Paperclip, Trash2, Search, X } from 'lucide-react';
import { toast } from 'sonner';
import PurchaseInvoiceForm, { emptyComprobante, comprobanteTotals } from '../components/PurchaseInvoiceForm';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const fmtMoney = (n) => '$ ' + Number(n || 0).toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtDate = (iso) => { if (!iso) return '-'; const p = String(iso).slice(0, 10).split('-'); return p.length === 3 ? p.reverse().join('/') : iso; };

export default function OtrasCompras() {
  const { user } = useOutletContext() || {};
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [suppliers, setSuppliers] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [expenseConcepts, setExpenseConcepts] = useState([]);
  const [withholdingTypes, setWithholdingTypes] = useState([]);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [comprobante, setComprobante] = useState(emptyComprobante());

  const canCreate = user?.role === 'admin' || user?.permissions?.compras_otras?.crear;
  const canDelete = user?.role === 'admin' || user?.role === 'supervisor' || user?.permissions?.compras_otras?.eliminar;
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteReason, setDeleteReason] = useState('');
  const [deleting, setDeleting] = useState(false);

  // v3.28.2 · Filtros
  const [filterText, setFilterText] = useState('');
  const [filterFrom, setFilterFrom] = useState('');
  const [filterTo, setFilterTo] = useState('');
  const [filterSupplier, setFilterSupplier] = useState('');
  const [filterImputation, setFilterImputation] = useState('');

  const filteredRows = useMemo(() => {
    const q = filterText.trim().toLowerCase();
    return rows.filter(r => {
      if (filterSupplier && (r.supplier_id || (r.supplier_snapshot || {}).supplier_id) !== filterSupplier) return false;
      if (filterImputation) {
        const imp = r.imputation_account_name || r.expense_concept_name || '';
        if (imp !== filterImputation) return false;
      }
      if (filterFrom && (r.fecha_comprobante || '') < filterFrom) return false;
      if (filterTo && (r.fecha_comprobante || '') > filterTo) return false;
      if (q) {
        const numero = `${r.tipo_comprobante || ''} ${r.punto_venta || ''}-${r.numero || ''}`.toLowerCase();
        const prov = ((r.supplier_snapshot || {}).business_name || '').toLowerCase();
        const imp = (r.imputation_account_name || r.expense_concept_name || '').toLowerCase();
        if (!numero.includes(q) && !prov.includes(q) && !imp.includes(q)) return false;
      }
      return true;
    });
  }, [rows, filterText, filterFrom, filterTo, filterSupplier, filterImputation]);

  const filteredTotals = useMemo(() => filteredRows.reduce((a, r) => ({
    neto: a.neto + (r.neto_gravado_total || 0),
    iva: a.iva + (r.iva_total || 0),
    total: a.total + (r.total || 0),
  }), { neto: 0, iva: 0, total: 0 }), [filteredRows]);

  const uniqueSuppliers = useMemo(() => {
    const map = new Map();
    rows.forEach(r => {
      const sid = r.supplier_id || (r.supplier_snapshot || {}).supplier_id;
      const name = (r.supplier_snapshot || {}).business_name || '—';
      if (sid && !map.has(sid)) map.set(sid, name);
    });
    return Array.from(map.entries())
      .map(([id, name]) => ({ id, name }))
      .sort((a, b) => a.name.localeCompare(b.name, 'es'));
  }, [rows]);

  const uniqueImputations = useMemo(() => {
    const s = new Set(rows.map(r => r.imputation_account_name || r.expense_concept_name).filter(Boolean));
    return Array.from(s).sort((a, b) => a.localeCompare(b, 'es'));
  }, [rows]);

  const clearFilters = () => {
    setFilterText(''); setFilterFrom(''); setFilterTo(''); setFilterSupplier(''); setFilterImputation('');
  };
  const anyFilterActive = filterText || filterFrom || filterTo || filterSupplier || filterImputation;

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      const r = await axios.delete(`${BACKEND_URL}/api/purchase-invoices/${deleteTarget.purchase_invoice_id}`,
        { withCredentials: true, data: { reason: deleteReason.trim() } });
      const revs = r.data?.reversal_entries || [];
      toast.success(revs.length
        ? `Comprobante eliminado · Contra-asiento #${revs.map(x => x.entry_number).join(', #')} generado`
        : 'Comprobante eliminado');
      setDeleteTarget(null); setDeleteReason('');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'No se pudo eliminar el comprobante');
    } finally { setDeleting(false); }
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${BACKEND_URL}/api/purchase-invoices`, { params: { source_type: 'other_purchase' }, withCredentials: true });
      setRows(r.data || []);
    } catch (e) { toast.error(e.response?.data?.detail || 'Error al cargar Otras Compras'); } finally { setLoading(false); }
  }, []);

  const loadMasters = useCallback(async () => {
    try { const s = await axios.get(`${BACKEND_URL}/api/suppliers/active`, { withCredentials: true }); setSuppliers(s.data || []); } catch { setSuppliers([]); }
    try { const a = await axios.get(`${BACKEND_URL}/api/accounting/accounts?only_postable=true`, { withCredentials: true }); setAccounts(a.data || []); } catch { setAccounts([]); }
    try { const e = await axios.get(`${BACKEND_URL}/api/treasury/expense-concepts`, { withCredentials: true }); setExpenseConcepts(e.data || []); } catch { setExpenseConcepts([]); }
    try { const w = await axios.get(`${BACKEND_URL}/api/treasury/withholding-types/active`, { withCredentials: true }); setWithholdingTypes(w.data || []); } catch { setWithholdingTypes([]); }
  }, []);

  useEffect(() => { load(); loadMasters(); }, [load, loadMasters]);

  const openNew = () => { setComprobante(emptyComprobante()); setOpen(true); };

  const save = async () => {
    const t = comprobanteTotals(comprobante);
    if (!comprobante.supplier_id) { toast.error('Seleccioná el proveedor'); return; }
    if (!comprobante.tipo_comprobante || !comprobante.punto_venta || !comprobante.numero || !comprobante.fecha_comprobante) { toast.error('Completá tipo, punto de venta, número y fecha del comprobante'); return; }
    if (t.total <= 0) { toast.error('Cargá el detalle del comprobante (neto/IVA)'); return; }
    if (comprobante.imputation_type === 'concept' && !comprobante.expense_concept_id) { toast.error('Seleccioná el concepto de gasto'); return; }
    if (comprobante.imputation_type === 'account' && !comprobante.imputation_account_code) { toast.error('Seleccioná la cuenta contable'); return; }
    setSaving(true);
    try {
      await axios.post(`${BACKEND_URL}/api/purchase-invoices/other`, {
        supplier_id: comprobante.supplier_id || null,
        supplier_business_name: comprobante.supplier_business_name || null,
        supplier_tax_id: comprobante.supplier_tax_id || null,
        supplier_tax_condition: comprobante.supplier_tax_condition || null,
        imputation_type: comprobante.imputation_type || 'account',
        imputation_account_code: comprobante.imputation_type === 'account' ? (comprobante.imputation_account_code || null) : null,
        expense_concept_id: comprobante.imputation_type === 'concept' ? (comprobante.expense_concept_id || null) : null,
        save_as_default: !!comprobante.save_as_default,
        fecha_comprobante: comprobante.fecha_comprobante,
        tipo_comprobante: comprobante.tipo_comprobante,
        punto_venta: comprobante.punto_venta || null,
        numero: comprobante.numero || null,
        items_iva: (comprobante.items_iva || []).filter(it => parseFloat(it.base_neta) > 0).map(it => ({ base_neta: parseFloat(it.base_neta), alicuota: parseFloat(it.alicuota) || 0 })),
        no_gravado: parseFloat(comprobante.no_gravado) || 0,
        exento: parseFloat(comprobante.exento) || 0,
        percepciones: (comprobante.percepciones || []).filter(p => parseFloat(p.monto) > 0).map(p => ({ tipo: p.tipo, withholding_type_id: p.withholding_type_id || null, base: parseFloat(p.base) || 0, alicuota: p.alicuota ? parseFloat(p.alicuota) : null, monto: parseFloat(p.monto) })),
        attachment_id: comprobante.attachment_id || null,
      }, { withCredentials: true });
      toast.success('Compra registrada · asiento generado y cuenta corriente actualizada');
      setOpen(false);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error al registrar la compra'); } finally { setSaving(false); }
  };

  return (
    <div className="space-y-6" data-testid="otras-compras-page">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <ShoppingBag className="w-7 h-7 text-amber-600" />
          <div>
            <h1 className="text-2xl font-bold">Otras Compras</h1>
            <p className="text-sm text-muted-foreground">Compras de gastos/servicios con imputación contable. Actualiza la cuenta corriente del proveedor.</p>
          </div>
        </div>
        {canCreate && <Button onClick={openNew} data-testid="otras-compras-new-btn"><Plus className="w-4 h-4 mr-2" />Nueva compra</Button>}
      </div>

      <Card><CardContent className="pt-6">
        {/* v3.28.2 · Filtros de búsqueda */}
        <div className="grid grid-cols-1 md:grid-cols-5 gap-3 mb-4">
          <div className="md:col-span-2">
            <Label className="text-xs">Buscar (proveedor / comprobante / imputación)</Label>
            <div className="relative">
              <Search className="w-4 h-4 absolute left-3 top-3 text-muted-foreground" />
              <Input className="pl-9" value={filterText} onChange={(e) => setFilterText(e.target.value)} placeholder="Ej: YAOPHARMA, FA 00005, Alimentos…" data-testid="otras-compras-filter-text" />
            </div>
          </div>
          <div>
            <Label className="text-xs">Desde</Label>
            <Input type="date" value={filterFrom} onChange={(e) => setFilterFrom(e.target.value)} data-testid="otras-compras-filter-from" />
          </div>
          <div>
            <Label className="text-xs">Hasta</Label>
            <Input type="date" value={filterTo} onChange={(e) => setFilterTo(e.target.value)} data-testid="otras-compras-filter-to" />
          </div>
          <div>
            <Label className="text-xs">Proveedor</Label>
            <select value={filterSupplier} onChange={(e) => setFilterSupplier(e.target.value)} className="flex h-10 w-full rounded-md border border-input bg-background px-2 py-2 text-sm" data-testid="otras-compras-filter-supplier">
              <option value="">Todos</option>
              {uniqueSuppliers.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
          </div>
          <div className="md:col-span-4">
            <Label className="text-xs">Imputación</Label>
            <select value={filterImputation} onChange={(e) => setFilterImputation(e.target.value)} className="flex h-10 w-full rounded-md border border-input bg-background px-2 py-2 text-sm" data-testid="otras-compras-filter-imputation">
              <option value="">Todas</option>
              {uniqueImputations.map(i => <option key={i} value={i}>{i}</option>)}
            </select>
          </div>
          <div className="flex items-end">
            {anyFilterActive && (
              <Button variant="outline" onClick={clearFilters} className="w-full" data-testid="otras-compras-filter-clear">
                <X className="w-4 h-4 mr-1" /> Limpiar
              </Button>
            )}
          </div>
        </div>
        <div className="flex justify-between items-center text-xs text-muted-foreground mb-2">
          <span data-testid="otras-compras-filter-count">
            {filteredRows.length === rows.length
              ? `${rows.length} comprobantes`
              : `${filteredRows.length} de ${rows.length} comprobantes`}
          </span>
          <span>
            Neto: <b>{fmtMoney(filteredTotals.neto)}</b> · IVA: <b>{fmtMoney(filteredTotals.iva)}</b> · Total: <b className="text-foreground">{fmtMoney(filteredTotals.total)}</b>
          </span>
        </div>
        {loading ? <p className="text-center py-8 text-muted-foreground">Cargando…</p> : (
          <Table data-testid="otras-compras-table">
            <TableHeader><TableRow>
              <TableHead className="w-24">Fecha</TableHead><TableHead>Comprobante</TableHead>
              <TableHead>Proveedor</TableHead><TableHead>Imputación</TableHead>
              <TableHead className="text-right">Neto</TableHead><TableHead className="text-right">IVA</TableHead>
              <TableHead className="text-right">Total</TableHead><TableHead className="w-10"></TableHead><TableHead className="w-10"></TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {filteredRows.map((r) => (
                <TableRow key={r.purchase_invoice_id} data-testid={`otras-compras-row-${r.purchase_invoice_id}`}>
                  <TableCell className="text-xs">{fmtDate(r.fecha_comprobante)}</TableCell>
                  <TableCell className="font-medium">{r.tipo_comprobante} {r.punto_venta || ''}-{r.numero || ''}</TableCell>
                  <TableCell>{(r.supplier_snapshot || {}).business_name || '—'}</TableCell>
                  <TableCell className="text-xs">{r.imputation_account_name || r.expense_concept_name || '—'}</TableCell>
                  <TableCell className="text-right">{fmtMoney(r.neto_gravado_total)}</TableCell>
                  <TableCell className="text-right">{fmtMoney(r.iva_total)}</TableCell>
                  <TableCell className="text-right font-medium">{fmtMoney(r.total)}</TableCell>
                  <TableCell>{r.attachment_id && <a href={`${BACKEND_URL}/api/purchase-invoices/image/${r.attachment_id}`} target="_blank" rel="noreferrer" className="text-amber-600"><Paperclip className="w-4 h-4" /></a>}</TableCell>
                  <TableCell>{canDelete && (
                    <button onClick={() => setDeleteTarget(r)} className="text-red-600 hover:text-red-800" title="Eliminar comprobante (solo si no está pagado)" data-testid={`otras-compras-delete-${r.purchase_invoice_id}`}>
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}</TableCell>
                </TableRow>
              ))}
              {filteredRows.length === 0 && (
                <TableRow><TableCell colSpan={9} className="text-center py-8 text-muted-foreground">
                  {rows.length === 0 ? 'Todavía no hay otras compras cargadas' : 'Ningún comprobante coincide con los filtros'}
                </TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        )}
      </CardContent></Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="otras-compras-dialog">
          <DialogHeader>
            <DialogTitle>Nueva compra</DialogTitle>
            <DialogDescription>Cargá el comprobante y elegí la imputación contable (cuenta o concepto de gasto).</DialogDescription>
          </DialogHeader>
          <PurchaseInvoiceForm
            value={comprobante}
            onChange={setComprobante}
            suppliers={suppliers}
            accounts={accounts}
            expenseConcepts={expenseConcepts}
            withholdingTypes={withholdingTypes}
            imputationMode={true}
            imputationSuggestionMode={true}
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
            <Button onClick={save} disabled={saving} data-testid="otras-compras-save-btn">{saving ? 'Guardando…' : 'Registrar compra'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!deleteTarget} onOpenChange={(v) => !v && setDeleteTarget(null)}>
        <DialogContent data-testid="otras-compras-delete-dialog">
          <DialogHeader>
            <DialogTitle>Eliminar comprobante</DialogTitle>
            <DialogDescription>
              ¿Confirmás eliminar <b>{deleteTarget?.tipo_comprobante} {deleteTarget?.punto_venta || ''}-{deleteTarget?.numero || ''}</b> por <b>{fmtMoney(deleteTarget?.total)}</b>?<br/>
              Si el comprobante tiene pagos aplicados, no se podrá eliminar. Se elimina el movimiento de cuenta corriente y el <b>asiento contable se reversa con un contra-asiento</b> con fecha de hoy (el original queda visible en el Libro Diario).
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label>Motivo de la eliminación *</Label>
            <Textarea value={deleteReason} onChange={(e) => setDeleteReason(e.target.value)} rows={2}
              placeholder="Ej.: comprobante cargado por duplicado" data-testid="otras-compras-delete-reason" />
            <p className="text-xs text-muted-foreground">Mínimo 10 caracteres. Queda registrado en el contra-asiento y en la auditoría.</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteTarget(null)}>Cancelar</Button>
            <Button variant="destructive" onClick={handleDelete} disabled={deleting || deleteReason.trim().length < 10} data-testid="otras-compras-delete-confirm">{deleting ? 'Eliminando…' : 'Eliminar'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
